import { Head, useForm } from "@inertiajs/react";
import { LoaderCircle } from "lucide-react";
import { FormEventHandler } from "react";

import InputError from "~/components/input-error";
import SsoLoginButton from "~/components/sso-login-button";
import TextLink from "~/components/text-link";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import AuthLayout, { type AuthBranding } from "~/layouts/auth-layout";
import { urlFor } from "~/client";

type LoginForm = {
  email: string;
  password: string;
  remember: boolean;
};

type LoginProps = {
  status?: string;
  canResetPassword: boolean;
  googleLoginEnabled?: boolean;
  ssoLoginEnabled?: boolean;
  ssoProviderName?: string | null;
  lastWorkspaceBrand?: AuthBranding | null;
};

export default function Login({
  status,
  canResetPassword,
  googleLoginEnabled = false,
  ssoLoginEnabled = false,
  ssoProviderName = null,
  lastWorkspaceBrand = null,
}: LoginProps) {
  const { data, setData, post, processing, errors } = useForm<Required<LoginForm>>({
    email: "",
    password: "",
    remember: false,
  });

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault();
    post(urlFor("login.store"), {
      onFinish: () => {
        data.password = "";
      },
    });
  };

  return (
    <AuthLayout
      title={
        lastWorkspaceBrand
          ? `Inicia sesión en ${lastWorkspaceBrand.companyName}`
          : "Inicia sesión en tu cuenta"
      }
      description="Ingresa tu correo electrónico y contraseña para iniciar sesión"
      branding={lastWorkspaceBrand ?? undefined}
    >
      <Head title="Iniciar sesión" />

      <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
        <div className="grid gap-6">
          {(googleLoginEnabled || ssoLoginEnabled) && (
            <>
              {ssoLoginEnabled && <SsoLoginButton providerName={ssoProviderName ?? undefined} />}
              {googleLoginEnabled && (
                <Button type="button" variant="outline" className="w-full" asChild>
                  <a href={urlFor("auth.google.redirect")}>
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden>
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    Continuar con Google
                  </a>
                </Button>
              )}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    O continúa con email
                  </span>
                </div>
              </div>
            </>
          )}

          <div className="grid gap-2">
            <Label htmlFor="email">Dirección de correo electrónico</Label>
            <Input
              id="email"
              type="email"
              required
              autoFocus
              tabIndex={1}
              autoComplete="email"
              value={data.email}
              onChange={(e) => setData("email", e.target.value)}
              placeholder="email@example.com"
            />
            <InputError message={errors.email} />
          </div>

          <div className="grid gap-2">
            <div className="flex items-center">
              <Label htmlFor="password">Contraseña</Label>
              {canResetPassword && (
                <TextLink
                  href={urlFor("password.request")}
                  className="ml-auto text-sm"
                  tabIndex={5}
                >
                  ¿Olvidaste tu contraseña?
                </TextLink>
              )}
            </div>
            <Input
              id="password"
              type="password"
              required
              tabIndex={2}
              autoComplete="current-password"
              value={data.password}
              onChange={(e) => setData("password", e.target.value)}
              placeholder="Contraseña"
            />
            <InputError message={errors.password} />
          </div>

          <div className="flex items-center space-x-3">
            <Checkbox
              id="remember"
              name="remember"
              checked={data.remember}
              onClick={() => setData("remember", !data.remember)}
              tabIndex={3}
            />
            <Label htmlFor="remember">Recordarme</Label>
          </div>

          <Button type="submit" className="mt-4 w-full" tabIndex={4} disabled={processing}>
            {processing && <LoaderCircle className="h-4 w-4 animate-spin" />}
            Iniciar sesión
          </Button>
        </div>

        <div className="text-muted-foreground text-center text-sm">
          ¿No tienes una cuenta?{" "}
          <TextLink href={urlFor("register.index")} tabIndex={6}>
            Regístrate
          </TextLink>
        </div>
      </form>

      {status && (
        <div className="mb-4 text-center text-sm font-medium text-green-600">{status}</div>
      )}
    </AuthLayout>
  );
}
